import React, { useState, useCallback, useRef } from 'react';
import { FileDown, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  fr: {
    default: 'Télécharger le catalogue PDF',
    loading: 'Préparation du PDF…',
    done: 'Téléchargement lancé ✓',
    tip: 'Vérifiez votre dossier "Téléchargements"',
    filename: 'Olion-Catalogue-2026-FR.pdf',
  },
  en: {
    default: 'Download PDF Catalog',
    loading: 'Preparing PDF…',
    done: 'Download started ✓',
    tip: 'Check your "Downloads" folder',
    filename: 'Olion-Catalogue-2026-EN.pdf',
  },
  ar: {
    default: 'تحميل كتالوج PDF',
    loading: 'يتم تحضير PDF...',
    done: 'بدأ التحميل ✓',
    tip: 'تحقق من مجلد "التنزيلات"',
    filename: 'Olion-Catalogue-2026-AR.pdf',
  },
};

/**
 * PDF download button — uses NATIVE browser download via a hidden <a download>.
 *
 * Why native download instead of fetch+blob?
 *  - fetch+blob buffers the entire 6 MB in memory before writing → can fail
 *    silently on slower connections / mobile browsers.
 *  - Native download shows the browser's OWN progress bar (bottom of screen)
 *    and streams to disk — much more reliable.
 *
 * We still show our loading toast so users know the ~15 s cold-start delay
 * is normal and not a broken button.
 */
export const DownloadCatalogButton = ({
  language = 'fr',
  className = '',
  variant = 'primary',
  short = false,
  testid = 'download-pdf-btn',
}) => {
  const [state, setState] = useState('idle'); // idle | loading | done
  const anchorRef = useRef(null);
  const L = LABELS[language] || LABELS.fr;

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      if (state === 'loading') return;

      const url = `${BACKEND_URL}/api/catalog/pdf?lang=${language}`;
      // Cache-buster query so browsers never serve a stale blank response.
      const bustedUrl = `${url}&t=${Date.now()}`;

      // Trigger native download via hidden <a>.
      const a = anchorRef.current || document.createElement('a');
      a.href = bustedUrl;
      a.download = L.filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      if (!anchorRef.current) document.body.appendChild(a);
      a.click();

      // Show loading UX. We can't detect exactly when the download finishes
      // (no event for native downloads), but backend serves cached PDFs in ~150ms
      // and cold-generates in ~8s, so 4s is a safe "done" signal on average.
      setState('loading');
      const loadingToastId = toast.loading(L.loading, { duration: 10000 });

      window.setTimeout(() => {
        setState('done');
        toast.success(L.done, {
          id: loadingToastId,
          description: L.tip,
          duration: 4000,
        });
        window.setTimeout(() => setState('idle'), 4000);
      }, 4000);
    },
    [language, state, L],
  );

  const iconClass = 'w-5 h-5 shrink-0';
  const isLoading = state === 'loading';
  const isDone = state === 'done';

  const styles = {
    primary:
      'bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white shadow-lg hover:shadow-xl',
    white: 'bg-white text-blue-700 hover:bg-gray-100 shadow-lg',
    ghost: 'bg-white/10 hover:bg-white/20 text-white border border-white/25',
    link: 'text-blue-700 hover:text-blue-900 !p-0 !bg-transparent !shadow-none !rounded-none !text-sm',
  };

  const label = isLoading ? L.loading : isDone ? L.done : L.default;

  const iconEl = isLoading ? (
    <Loader2 className={`${iconClass} animate-spin`} />
  ) : isDone ? (
    <CheckCircle2 className={iconClass} />
  ) : (
    <FileDown className={`${iconClass} group-hover:translate-y-1 transition-transform`} />
  );

  return (
    <>
      <a ref={anchorRef} style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        data-testid={testid}
        aria-busy={isLoading}
        className={`group inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 disabled:opacity-80 disabled:cursor-wait ${
          styles[variant] || styles.primary
        } ${short ? 'text-sm' : 'text-base px-8 py-4'} ${className}`}
      >
        {iconEl}
        <span className="whitespace-nowrap">{label}</span>
      </button>
    </>
  );
};
